import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Users, MagnifyingGlass, UploadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import LazyAvatar from "@/components/LazyAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ProfilePhotoCropper from "@/components/ProfilePhotoCropper";

// Read a picked file as a data URL so the cropper can render it.
async function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("Could not read the image"));
    r.readAsDataURL(file);
  });
}

const empty = { name: "", role: "", bio: "", photo_url: "", social_url: "", sort_order: 0 };

export default function TeamAdmin() {
  const [rows, setRows] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [t, s] = await Promise.all([api.get("/team"), api.get("/speakers")]);
      setRows(t.data);
      setSpeakers(s.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  // Match the current form name against the speaker list (case-insensitive,
  // trims whitespace). Returns the matched speaker or null.
  const matchingSpeaker = (() => {
    const n = (form.name || "").trim().toLowerCase();
    if (!n) return null;
    return speakers.find((s) => (s.name || "").trim().toLowerCase() === n) || null;
  })();

  const pullFromSpeaker = async () => {
    if (!matchingSpeaker) return;
    // Speaker list responses no longer carry photo_url — fetch the actual data
    // URL on demand from the per-speaker endpoint.
    let photo_url = "";
    if (matchingSpeaker.has_photo) {
      try { const r = await api.get(`/speakers/${matchingSpeaker.id}/photo`); photo_url = r.data?.photo_url || ""; } catch { /* ignore */ }
    }
    setForm((cur) => ({
      ...cur,
      photo_url: photo_url || cur.photo_url,
      bio: matchingSpeaker.bio || cur.bio,
    }));
    toast.success(`Pulled photo & bio from ${matchingSpeaker.name}'s speaker profile`);
  };

  const openNew = () => {
    setEditing(null);
    // Sort new entries to the bottom by default.
    const nextOrder = (rows[rows.length - 1]?.sort_order || 0) + 10;
    setForm({ ...empty, sort_order: nextOrder });
    setOpen(true);
  };
  const openEdit = async (m) => {
    setEditing(m);
    // Fetch the existing photo data URL so the modal can show + replace it.
    let photo_url = "";
    if (m.has_photo) {
      try { const r = await api.get(`/team/${m.id}/photo`); photo_url = r.data?.photo_url || ""; } catch { /* ignore */ }
    }
    setForm({
      name: m.name || "",
      role: m.role || "",
      bio: m.bio || "",
      photo_url,
      social_url: m.social_url || "",
      sort_order: m.sort_order || 0,
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editing) await api.patch(`/team/${editing.id}`, form);
      else await api.post("/team", form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team list?`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success(`${m.name} removed`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      e.target.value = "";
      return;
    }
    try {
      const url = await fileToDataUrl(file);
      setCropSrc(url);
    } catch (err) {
      toast.error(err.message || "Could not read the image");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div data-testid="team-admin">
      <p className="text-xs text-[#7A828C] mb-3">
        Shown to delegates at the bottom of the Info tab. Order is controlled by the &quot;Sort order&quot; field (lower = higher up).
      </p>
      <Button
        onClick={openNew}
        className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24] mb-3"
        data-testid="admin-new-team-btn"
      >
        <Plus size={16} className="mr-1" /> New team member
      </Button>
      <div className="space-y-2">
        {rows.map((m) => (
          <div
            key={m.id}
            className="p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm flex items-center gap-3"
            data-testid={`admin-team-row-${m.id}`}
          >
            <LazyAvatar
              endpoint={`/team/${m.id}/photo`}
              hasPhoto={m.has_photo}
              name={m.name}
              className="w-10 h-10 rounded-full flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{m.name}</div>
              <div className="text-xs text-[#7A828C] truncate">
                {m.role || <span className="italic">no role</span>} · sort {m.sort_order}
              </div>
            </div>
            <button
              onClick={() => openEdit(m)}
              className="w-10 h-10 grid place-items-center rounded-sm hover:bg-[#F0F2F7]"
              data-testid={`edit-team-${m.id}`}
              aria-label={`Edit ${m.name}`}
            >
              <PencilSimple size={16} />
            </button>
            <button
              onClick={() => del(m)}
              className="w-10 h-10 grid place-items-center rounded-sm hover:bg-[#FEECEC] text-[#DC4F48]"
              data-testid={`delete-team-${m.id}`}
              aria-label={`Delete ${m.name}`}
            >
              <Trash size={16} />
            </button>
          </div>
        ))}
        {rows.length === 0 && <div className="text-[#7A828C] text-center py-6 text-sm">No team members yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E5E9F0] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit team member" : "New team member"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="team-name">Name</Label>
              <Input
                id="team-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                className="rounded-sm mt-1"
                data-testid="form-team-name"
              />
            </div>
            <div>
              <Label htmlFor="team-role">Role / title</Label>
              <Input
                id="team-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder="e.g. Co-founder, Editor"
                className="rounded-sm mt-1"
                data-testid="form-team-role"
              />
            </div>
            {matchingSpeaker && (
              <button
                type="button"
                onClick={pullFromSpeaker}
                className="w-full p-2 border border-dashed border-[#000000]/40 rounded-sm text-left hover:bg-[#E6F8FC] transition-colors"
                data-testid="form-team-pull-speaker"
              >
                <div className="flex items-center gap-2 text-xs">
                  <MagnifyingGlass size={14} className="text-[#000000] flex-shrink-0" />
                  <span className="text-[#000000] font-mono-time uppercase tracking-widest text-[10px]">Match found</span>
                  <span className="text-[#5a5a5a] truncate">{matchingSpeaker.name} also has a speaker profile</span>
                </div>
                <div className="text-xs text-[#1c1c24] mt-1">
                  Click to pull <strong>photo</strong>{matchingSpeaker.bio ? " and bio" : ""} from their speaker entry
                </div>
              </button>
            )}
            <div>
              <Label>Photo</Label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onUploadPhoto}
                className="hidden"
                data-testid="form-team-photo-input"
              />
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {form.photo_url && (
                  <img src={form.photo_url} alt="preview" className="w-16 h-16 rounded-full object-cover border border-[#E5E9F0]" data-testid="form-team-photo-preview" />
                )}
                <Button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
                  data-testid="form-team-photo-upload-btn"
                >
                  <UploadSimple size={14} className="mr-1.5" />
                  {form.photo_url ? "Replace photo" : "Upload photo"}
                </Button>
                {form.photo_url && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setForm({ ...form, photo_url: "" })}
                    className="rounded-sm"
                    data-testid="form-team-photo-clear-btn"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <p className="text-xs text-[#7A828C] mt-2">JPG, PNG, HEIC or WEBP. You&apos;ll be able to pan and zoom to frame it.</p>
              <details className="text-xs mt-2">
                <summary className="cursor-pointer text-[#7A828C] hover:text-[#000000]">…or paste a photo URL instead</summary>
                <Input
                  id="team-photo"
                  value={form.photo_url?.startsWith("data:") ? "" : form.photo_url}
                  onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                  placeholder="https://…"
                  className="rounded-sm mt-2 font-mono-time text-xs"
                  data-testid="form-team-photo"
                />
              </details>
            </div>
            <div>
              <Label htmlFor="team-social">Profile / social URL (optional)</Label>
              <Input
                id="team-social"
                value={form.social_url}
                onChange={(e) => setForm({ ...form, social_url: e.target.value })}
                placeholder="https://bsky.app/… or https://twitter.com/…"
                className="rounded-sm mt-1 font-mono-time text-xs"
                data-testid="form-team-social"
              />
            </div>
            <div>
              <Label htmlFor="team-bio">Short bio (1-2 sentences)</Label>
              <Textarea
                id="team-bio"
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                rows={3}
                className="rounded-sm mt-1"
                data-testid="form-team-bio"
              />
            </div>
            <div>
              <Label htmlFor="team-order">Sort order</Label>
              <Input
                id="team-order"
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0", 10) })}
                className="rounded-sm mt-1 w-32"
                data-testid="form-team-order"
              />
              <p className="text-xs text-[#7A828C] mt-1">Lower number = higher on the list. Use multiples of 10 to leave room for re-ordering.</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={save}
              className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
              data-testid="form-team-save"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProfilePhotoCropper
        open={!!cropSrc}
        imageDataUrl={cropSrc}
        onApply={(url) => {
          setForm((f) => ({ ...f, photo_url: url }));
          setCropSrc(null);
          toast.success("Photo ready - click Save to keep it");
        }}
        onCancel={() => setCropSrc(null)}
      />
    </div>
  );
}
